import React, { Fragment, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Dropzone } from './../../../Components/Form/Dropzone'
import { CSVError, processCsvFile } from './../../../Utils/Misc'
import { ModalHeader } from './../../../Components/Modal/ModalHeader'
import { ModalTitle } from './../../../Components/Modal/ModalTitle'
import { ModalBody } from './../../../Components/Modal/ModalBody'
import { ModalFooter } from './../../../Components/Modal/ModalFooter'
import SubmitButton from '../../../Components/Form/SubmitButton/index'
import LinearProgressBar from '../../../Components/LinearProgressBar'

/**
 * @typedef ImportMissionsProps
 */
const ImportMissionsView = props => {
  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState([])
  const [progress, setProgress] = useState(0)
  const [isShowProgress, setIsShowProgress] = useState(false)

  const onProgress = ({ processedAmount, totalAmount }) => {
    setProgress(Math.floor((processedAmount / totalAmount) * 100))
  }

  const onUploadFile = entryFiles => {
    let file = null
    if (typeof entryFiles[0] === 'undefined') {
      // Windows env
      const input = document.querySelector(
        '#ImportMissionsViewDropZoneContainer input',
      )
      if (!input) return null
      file = input.files.length && input.files[0]
    } else {
      file = entryFiles[0]
    }
    if (!file) return null
    setFile(file)
  }
  const processFile = async () => {
    if (!file) return
    const csvData = await processCsvFile(file)

    const errors = []
    const missionDtos = csvData.data.filter((d, i) => {
      // If all cells are empty then skip it
      const emptyRow = Object.keys(d).every(key => d[key].trim() == '')
      if (emptyRow) return false
      // It would be rude not to validate since we're here
      try {
        props.validateRow(d)
      } catch (e) {
        const csvError = new CSVError(e.message, i + 2)
        csvError.suggestion = e.suggestion
        errors.push(csvError)
      }
      return true
    })
    if (errors.length) return setErrors(errors)
    setIsShowProgress(true)

    props.onImportSuccess(missionDtos, onProgress)

    setIsShowProgress(false)
  }

  return (
    <Fragment>
      <ModalHeader>
        <ModalTitle>Import missions</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <React.Fragment>
          <Row>
            <Col xs={6}>
              <a
                href="javascript:;" // eslint-disable-line
                onClick={props.downloadSampleCSV}
                cypress_id="roster-import-missions-modal-download-csv-template-link"
              >
                Download the csv template
              </a>
              <br />
              <br />
              {props.isMasterSchedule ? (
                <div style={{ fontSize: 12 }}>
                  <div cypress_id="roster-import-missions-modal-pay-adjustment-note">
                    The `PAY_ADJUSTMENT` column can be used to cover additional
                    costs, for example travel costs.
                  </div>
                  <br />
                  <div>No currency sign in the costs.</div>
                  <div>Format: 10.5</div>
                  <br />
                  <div>No space in the numbers. </div>
                  <div>Format: 1000</div>
                  <br />
                  <div>Use dot for decimals, not comma.</div>
                  <div>Format: 1000.00</div>
                  <br />
                  <div>CLIENT_DISTRIBUTION_COST (H2H):</div>
                  <div>mission_cost * checkout_flyer_quantity</div>
                  <br />
                  <div>CLIENT_DISTRIBUTION_COST (D2D):</div>
                  <div>postage_cost * checkout_flyer_quantity</div>
                  <br />
                  <div>All uploaded missions will be with status COMPLETED</div>
                </div>
              ) : (
                <div style={{ fontSize: 9 }}>
                  <div cypress_id="roster-import-missions-modal-pay-adjustment-note">
                    The `PAY_ADJUSTMENT` column can be used to cover additional
                    costs, for example travel costs.
                  </div>
                </div>
              )}
            </Col>
            <Col xs={6}>
              <div id="ImportMissionsViewDropZoneContainer">
                <Dropzone
                  mainLabel="Missions File"
                  input={{
                    value: '',
                    onChange: onUploadFile,
                  }}
                  meta={{}}
                  cypress_id="roster-import-missions-csv"
                />
              </div>
            </Col>
          </Row>
        </React.Fragment>
      </ModalBody>
      {errors.length ? (
        <ImportMissionsErrorsView
          errors={errors}
          onOkay={() => setErrors([])}
        />
      ) : null}
      {progress || isShowProgress ? (
        <LinearProgressBar
          variant="determinate"
          value={progress}
          showValue={true}
          isLoading={false}
          styles={{
            paddingTop: 40,
            marginRight: 86,
            marginLeft: 50,
          }}
        />
      ) : null}
      <ModalFooter>
        <SubmitButton
          value="cancel"
          onClick={props.onClose}
          cypress_id="roster-missions-import-modal-cancel-button"
          disabled={progress || isShowProgress}
        />
        <SubmitButton
          value="Confirm"
          onClick={processFile}
          cypress_id="roster-missions-import-modal-submit-button"
          disabled={errors.length !== 0 || progress || isShowProgress || !file}
        />
      </ModalFooter>
    </Fragment>
  )
}

const ImportMissionsErrorsView = props => {
  return (
    <Fragment>
      <ModalBody>
        <h4>Import Failed</h4>
        <p>Please review the below the lines that have an error:</p>
        {props.errors.map(err => {
          validateCSVError(err)
          return (
            <div key={err.lineNumber} className="modal-error-line">
              <p>
                <strong>Line {err.lineNumber || 'N/A'}: </strong>{' '}
                <small>{err.message}</small>
              </p>
              {err.suggestion && (
                <p>
                  <em>
                    <strong>{err.suggestion}</strong>
                  </em>
                </p>
              )}
            </div>
          )
        })}
      </ModalBody>
    </Fragment>
  )
}

const validateCSVError = function(error) {
  if (error instanceof CSVError === false)
    throw new Error("Error is not an 'import mission' error object.")
}

export default ImportMissionsView
